import { handleError } from '@slr/shared-library';
import { API_SOLUTION1_SERVICE_URL, API_USER_SERVICE_URL } from 'configs';
import { useCallback } from 'react';
import useGetApiConfig from '../useGetApiConfig';
import {
  OrganizationOfferCountsResponse,
  PlatformAdminOrganizationPage
} from './platformAdminOrganization.types';

const buildQuery = (
  parameters: Record<string, string | number | boolean | undefined>
) => {
  const searchParams = new URLSearchParams();
  Object.entries(parameters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.set(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export const usePlatformAdminOrganizationsApi = () => {
  const userServiceApiConfig = useGetApiConfig(API_USER_SERVICE_URL);
  const backendApiConfig = useGetApiConfig(API_SOLUTION1_SERVICE_URL);

  const authorizedFetch = useCallback(
    async (
      apiConfig: ReturnType<typeof useGetApiConfig>,
      path: string,
      init?: RequestInit
    ) => {
      const accessToken =
        typeof apiConfig.accessToken === 'function'
          ? await apiConfig.accessToken()
          : apiConfig.accessToken;
      const response = await fetch(`${apiConfig.basePath}${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(accessToken ? { Authorization: accessToken } : {}),
          ...init?.headers
        }
      });
      if (!response.ok) {
        let message = response.statusText;
        try {
          const errorBody = await response.json();
          message = errorBody?.message || errorBody?.detail || message;
        } catch {}
        const error = new Error(message) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }
      const responseText = await response.text();
      if (!responseText) {
        return undefined;
      }
      return JSON.parse(responseText);
    },
    []
  );

  const searchOrganizations = useCallback(
    (search: string | undefined, page: number, count: number) =>
      authorizedFetch(
        userServiceApiConfig,
        `/admin/organizations${buildQuery({ search, page, count })}`
      )
        .then((data) => data as PlatformAdminOrganizationPage)
        .catch(handleError),
    [authorizedFetch, userServiceApiConfig]
  );

  const getOrganizationOfferCounts = useCallback(
    (organizationIds: string[]) => {
      if (organizationIds.length === 0) {
        return Promise.resolve({ counts: [] } as OrganizationOfferCountsResponse);
      }
      const query = new URLSearchParams();
      organizationIds.forEach((organizationId) => {
        query.append('organizationIds', organizationId);
      });
      return authorizedFetch(
        backendApiConfig,
        `/admin/organizations/offer-counts?${query.toString()}`
      )
        .then((data) => data as OrganizationOfferCountsResponse)
        .catch(handleError);
    },
    [authorizedFetch, backendApiConfig]
  );

  return {
    searchOrganizations,
    getOrganizationOfferCounts
  };
};
